import { useLayoutEffect, useRef, type ReactNode } from 'react';

export interface DrawerProps {
  open: boolean;
  /**
   * The drawer was asked to close (Escape, the backdrop or its close button). It closes when `open` turns false, so
   * the owner may ask first ("Discard changes?") or keep it open.
   */
  onClose: () => void;
  /** The drawer's title, which also names its dialog. Read once: a drawer with another title is a new element. */
  heading: string;
  closeLabel: string;
  side?: 'left' | 'right' | 'bottom';
  /** About 640 px wide instead of orno's 420 px, and the full width below 768 px: for a form. */
  wide?: boolean;
  children: ReactNode;
}

/** The drawer element's method every way of closing it calls. */
type Closable = HTMLElement & { close: () => void };

/** What, open inside the drawer, takes Escape for itself: a dialog (a confirmation) or an expanded list (a select). */
const OPEN_INSIDE = 'orno-dialog[open], [aria-expanded="true"]';

/**
 * `<orno-drawer>` for React, controlled by `open`:
 *
 * - `open` is an attribute the element reads, but also the name of its `open()` method, so React would assign the
 *   property; it is toggled through a ref.
 * - Escape, the backdrop and the ✕ all call the element's `close()`, which would hide it at once; this element's own
 *   `close()` only reports the request (`onClose`), and the drawer hides when `open` turns false. A `close` event
 *   bubbling from inside therefore never counts, nor does an Escape that closes a dialog or select inside it.
 * - The element moves its children into its sheet, so they sit in one stable `<div>` that is moved instead.
 */
export function Drawer({
  open,
  onClose,
  heading,
  closeLabel,
  side = 'right',
  wide = false,
  children,
}: DrawerProps) {
  const ref = useRef<HTMLElement>(null);
  const requestClose = useRef(onClose);

  useLayoutEffect(() => {
    requestClose.current = onClose;
  }, [onClose]);

  // orno's drawer, dialog and select all answer Escape (the drawer on `document`, first): an Escape meant for a dialog
  // or an open select inside the drawer is theirs alone. Whether one was open is read before any of them acts, in the
  // capture phase, as the dialog and the select close themselves on the same key.
  useLayoutEffect(() => {
    const element = ref.current as Closable | null;
    if (!element) return;
    let escapeTaken = false;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      escapeTaken = element.querySelector(OPEN_INSIDE) !== null;
      window.setTimeout(() => {
        escapeTaken = false;
      });
    };
    element.close = () => {
      if (escapeTaken || element.querySelector('orno-dialog[open]') !== null) return;
      requestClose.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  useLayoutEffect(() => {
    ref.current?.toggleAttribute('open', open);
  }, [open]);

  return (
    <orno-drawer
      ref={ref}
      heading={heading}
      close-label={closeLabel}
      side={side}
      class={wide ? 'app-drawer--wide' : undefined}
    >
      <div className="orno-content">{children}</div>
    </orno-drawer>
  );
}
